import { SearchIcon } from "lucide-react";
import styles from "./JobTab.module.scss";
import classNames from "classnames/bind";
import { useEffect, useState, useMemo } from "react";
import { MenuVerticalIcon, PlusIcon } from "~/assets/icons";
import BaseTableV2 from "~/components/common/base-table-v2/BaseTableV2";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetCustomerJobs } from "~/thunks/crm/crmThunk";
import { handleCreateAddJob, handleDeleteJob } from "~/thunks/job/jobThunk";
import { IJobEntity } from "~/apis/crm";
import { useNavigate } from "react-router-dom";
import AddJobFromClientModal from "./AddJobFromClientModal";
import { notify } from "~/utils/helper";
import { crmActions } from "~/thunks/crm/crmSlice";
import { Menu, MenuItem } from "@mui/material";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import { adminRouteAbsolute } from "~/utils/constants/route";

const cx = classNames.bind(styles);

const headCells = [
  { id: "createdDate", label: "Created Date", width: "7%" },
  { id: "jobName", label: "Job Name", width: "7%" },
  { id: "status", label: "Status", width: "8%" },
  { id: "salesRep", label: "Sales Rep", width: "7%" },
  { id: "primaryContact", label: "Primary Contact", width: "7%" },
  { id: "jobNumber", label: "Job Number", width: "7%" },
  { id: "address", label: "Address", width: "15%" },
  { id: "recentInvoice", label: "Recent Invoice", width: "8%" },
  { id: "recentEstimate", label: "Recent Estimate", width: "8%" },
  { id: "recentReceived", label: "Recent Received", width: "8%" },
  { id: "balanceDue", label: "Balance Due", width: "8%" },
  { id: "assignedTo", label: "Assigned To", width: "5%" },
  { id: "action", label: "Action", width: "5%" },
];

const JobTab = () => {
  const dispatch = useAppDispatch();
  const { customerJobs, jobsLoading, jobsCurrentPage, jobsTotalPage, jobsTotalItems, client } = useAppSelector((state) => state.crm);
  const [textSearch, setTextSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedJob, setSelectedJob] = useState<IJobEntity | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();
  
  // Get customer ID from URL
  const customerId = window.location.pathname.split("/").pop() || "";

  useEffect(() => {
    if (customerId) {
      dispatch(handleGetCustomerJobs({ 
        customerId, 
        params: { 
          page: currentPage, 
          limit: 10
        } 
      }));
    }
  }, [dispatch, customerId, currentPage]);


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextSearch(e.target.value);
  };

  const filteredJobs = useMemo(() => {
    if (!textSearch.trim()) {
      return customerJobs;
    }

    const searchLower = textSearch.toLowerCase();
    return customerJobs.filter((job) => {
      const jobName = job.jobName?.toLowerCase() || "";
      const jobNumber = job.jobNumber?.toLowerCase() || "";
      const address = job.address?.toLowerCase() || "";
      const salesRepName = job.salesRep?.name?.toLowerCase() || "";
      const assigneeName = job.assignedTo?.name?.toLowerCase() || "";
      const status = job.status?.toLowerCase() || "";
      const primaryContact = job.primaryContact?.toLowerCase() || "";

      return (
        jobName.includes(searchLower) ||
        jobNumber.includes(searchLower) ||
        address.includes(searchLower) ||
        salesRepName.includes(searchLower) ||
        assigneeName.includes(searchLower) ||
        status.includes(searchLower) ||
        primaryContact.includes(searchLower)
      );
    });
  }, [customerJobs, textSearch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "---";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'in progress':
      case 'progress':
        return 'in-progress';
      case 'scheduled':
        return 'scheduled';
      case 'completed':
        return 'completed';
      default:
        return '';
    }
  };

  const handleAddJob = () => {
    setShowAddJobModal(true);
  };

  const handleCancelAddJob = () => {
    setShowAddJobModal(false);
  };

  const handleSubmitAddJob = async (data: any) => {
    try {
      await dispatch(handleCreateAddJob(data)).unwrap();
      notify("success", "Job created successfully!");
      setShowAddJobModal(false);
      dispatch(crmActions.changeNeedRefreshData(true));
      // Refresh jobs list
      if (customerId) {
        dispatch(handleGetCustomerJobs({ 
          customerId, 
          params: { 
            page: currentPage, 
            limit: 10
          } 
        }));
      }
    } catch (error) {
      notify("error", "Failed to create job!");
    }
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, job: IJobEntity) => {
    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDetailClick = () => {
    if (selectedJob) {
      navigate(adminRouteAbsolute.job.detail(selectedJob.id));
    }
    setAnchorEl(null);
    setSelectedJob(null);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedJob) {
      try {
        await dispatch(handleDeleteJob(selectedJob.id)).unwrap();
        notify("success", "Job deleted successfully!");
        setShowDeleteModal(false);
        setSelectedJob(null);
        // Refresh jobs list
        if (customerId) {
          dispatch(handleGetCustomerJobs({ 
            customerId, 
            params: { 
              page: currentPage, 
              limit: 10
            } 
          }));
        }
      } catch (error) {
        notify("error", "Failed to delete job!");
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedJob(null);
  };
  return (
    <div className={cx("job-tab-container")}>
      <div className={cx("header")}>
        <div className={cx("search")}>
          <SearchIcon className={cx("search-icon")} />
          <input
            type="text"
            placeholder="Search jobs..."
            className={cx("search-input")}
            value={textSearch}
            onChange={handleSearchChange}
          />
        </div>

        <button className={cx("add-button")} onClick={handleAddJob}>
          <PlusIcon className={cx("add-icon")} />
          <p className={cx("add-text")}>Add Job</p>
        </button>
      </div>

      <div>
        {jobsLoading ? (
          <div className={cx("loading")}>
            <p>Loading jobs...</p>
          </div>
        ) : filteredJobs.length === 0 && textSearch.trim() ? (
          <div className={cx("no-jobs")}>
            <p>No jobs found matching "{textSearch}"</p>
          </div>
        ) : (
          <BaseTableV2
            data={filteredJobs}
            headCells={headCells}
            page={jobsCurrentPage}
            totalPages={jobsTotalPage}
            totalItems={textSearch.trim() ? filteredJobs.length : jobsTotalItems}
            showPagination={!textSearch.trim()}
            onChangePage={handlePageChange}
            renderCell={(job: IJobEntity, index: number) => (
              <>
                <td className={cx("table-body-cell", "w-[7%]")}>
                  <p className={cx("text-content")}>{formatDate(job.createdAt)}</p>
                </td>
                <td className={cx("table-body-cell", "w-[7%]")}>
                  <p className={cx("text-content")}>{job.jobName}</p>
                </td>
                <td className={cx("table-body-cell", "w-[8%]")}>
                  <div className={cx("status-box", getStatusClass(job.status))}>
                    <p className={cx("text-content")}>{job.status}</p>
                  </div>
                </td>
                <td className={cx("table-body-cell", "w-[7%]")}>
                  <p className={cx("text-content")}>{job.salesRep?.name || "---"}</p>
                </td>
                <td className={cx("table-body-cell", "w-[7%]")}>
                  <p className={cx("text-content")}>{job.primaryContact || "---"}</p>
                </td>
                <td className={cx("table-body-cell", "w-[7%]")}>
                  <p className={cx("text-content")}>{job.jobNumber}</p>
                </td>
                <td className={cx("table-body-cell", "w-[15%]")}>
                  <p className={cx("text-content")}>{job.address || "---"}</p>
                </td>
                <td className={cx("table-body-cell", "w-[8%]")}>
                  <p className={cx("text-content")}>{job.recentInvoice || "---"}</p>
                </td>
                <td className={cx("table-body-cell", "w-[8%]")}>
                  <p className={cx("text-content")}>
                    {job.recentEstimate && job.recentEstimate.totalQuote !== undefined && job.recentEstimate.totalQuote !== null
                      ? `$${job.recentEstimate.totalQuote}`
                      : "---"}
                  </p>
                </td>
                <td className={cx("table-body-cell", "w-[8%]")}>
                  <p className={cx("text-content")}>${job.paymentReceived || 0}</p>
                </td>
                <td className={cx("table-body-cell", "w-[8%]")}>
                  <p className={cx("text-content")}>${job.balance || 0}</p>
                </td>
                <td className={cx("table-body-cell", "w-[5%]")}>
                  <p className={cx("text-content")}>{job.assignedTo?.name || "---"}</p>
                </td>
                <td className={cx("table-body-cell", "w-[5%]")}>
                  <button 
                    className={cx("action-button")}
                    onClick={(e) => handleActionClick(e, job)}
                  >
                    <MenuVerticalIcon className={cx("action-icon")} />
                  </button>
                </td>
              </>
            )}
          />
        )}
      </div>
      
      {showAddJobModal && (
        <AddJobFromClientModal
          isOpen={showAddJobModal}
          onCancel={handleCancelAddJob}
          onSubmit={handleSubmitAddJob}
          clientId={customerId}
          clientAddress={client.address || ""}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleDetailClick}>Detail</MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: '#FF2D55' }}>Delete</MenuItem>
      </Menu>

      <ModalAlertDelete
        open={showDeleteModal}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        description={`Are you sure you want to delete job "${selectedJob?.jobName}"?`}
      />
    </div>
  );
};

export default JobTab;
